import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ServiceCost } from "../benefitsSummary.types";

export const NetworkChip = ({
  network,
}: {
  network: ServiceCost["network"];
}) =>
  network === "in" ? (
    <Chip label="In network" color="success" size="small" />
  ) : network === "out" ? (
    <Chip label="Out of network" color="warning" size="small" />
  ) : null;

const Cost = ({ service }: { service: ServiceCost }) => (
  <Box component="span" sx={{ whiteSpace: "nowrap" }}>
    <Typography
      component="span"
      variant="h6"
      sx={{
        fontWeight: 600,
        color: service.selfPay ? "text.primary" : "primary.main",
      }}
    >
      {service.amount}
    </Typography>
    {service.unit && (
      <>
        {service.selfPay ? ", " : " "}
        <Typography component="span" variant="body2" color="text.secondary">
          {service.unit}
        </Typography>
      </>
    )}
  </Box>
);

const ServiceNote = ({ note }: { note: string }) => (
  <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
    <InfoOutlinedIcon
      sx={{ fontSize: 16, mt: "2px", color: "text.secondary" }}
    />
    <Typography variant="body2" color="text.secondary">
      {note}
    </Typography>
  </Stack>
);

const ServiceCostTable = ({ services }: { services: ServiceCost[] }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  if (!isDesktop) {
    return (
      <Stack divider={<Divider flexItem />} spacing={2}>
        {services.map((service) => (
          <Box key={service.id} data-testid="service-cost-row">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {service.service}
              </Typography>
              <NetworkChip network={service.network} />
            </Stack>
            <Box sx={{ mt: 0.5 }}>
              <Cost service={service} />
            </Box>
            {service.note && <ServiceNote note={service.note} />}
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Table aria-label="What your plan lists for each service">
      <TableHead>
        <TableRow>
          <TableCell sx={{ color: "text.secondary", pl: 0 }}>Service</TableCell>
          <TableCell sx={{ color: "text.secondary" }}>Network</TableCell>
          <TableCell sx={{ color: "text.secondary", pr: 0 }}>
            Your cost
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {services.map((service) => (
          <TableRow
            key={service.id}
            data-testid="service-cost-row"
            sx={{ "&:last-child td": { borderBottom: 0 } }}
          >
            <TableCell sx={{ pl: 0, width: "45%" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {service.service}
              </Typography>
              {service.note && <ServiceNote note={service.note} />}
            </TableCell>
            <TableCell sx={{ width: "20%" }}>
              <NetworkChip network={service.network} />
            </TableCell>
            <TableCell sx={{ pr: 0 }}>
              <Cost service={service} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ServiceCostTable;
